import { Capacitor } from '@capacitor/core';
import {
  MediaSession as MediaSessionPlugin,
  type MediaSessionAction as PluginAction,
  type MediaSessionPlugin as MediaSessionPluginApi,
} from '@capgo/capacitor-media-session';

import type { MediaSessionLike } from './audioOverviewPlayer';
import { NATIVE_ARTWORK } from './nativeArtwork';

/**
 * Where the Audio Overview engine publishes its lock-screen controls (ORA-735).
 *
 * - Web and iOS: `navigator.mediaSession`. WKWebView turns it into the iOS Now
 *   Playing / lock-screen controls itself (the plugin also defers to it there).
 * - Android app: the `@capgo/capacitor-media-session` plugin. Its foreground
 *   service (type `mediaPlayback`) keeps the app alive while an episode plays,
 *   and its media notification is the lock-screen and Bluetooth control.
 *
 * The native shells load the live web app, so older installs without the
 * plugin run this code too: they fall back to `navigator.mediaSession`, which
 * is what they did before.
 */

export const NATIVE_MEDIA_SESSION_PLUGIN = 'MediaSession';

/**
 * The plugin's notification draws its skip buttons with "30" icons, so a
 * press from it skips 30 s. The engine's default (15 s) is for sources that
 * say nothing.
 */
export const NATIVE_SEEK_OFFSET_SEC = 30;

export type MediaSessionPluginLike = Pick<
  MediaSessionPluginApi,
  'setMetadata' | 'setPlaybackState' | 'setActionHandler' | 'setPositionState'
>;

const PLUGIN_ACTIONS = new Set<MediaSessionAction>([
  'play',
  'pause',
  'seekbackward',
  'seekforward',
  'previoustrack',
  'nexttrack',
  'seekto',
  'stop',
]);

const isPluginAction = (action: MediaSessionAction): action is PluginAction =>
  PLUGIN_ACTIONS.has(action);

/** Bridge calls are fire-and-forget: a failed one must never stop playback. */
const send = (call: () => Promise<unknown>) => {
  try {
    void call().catch(() => undefined);
  } catch {
    // A synchronous throw is as harmless as a rejection.
  }
};

/**
 * How long a paused episode keeps its notification. After that the service
 * stops: a student who has not come back in ten minutes is not about to
 * resume from the lock screen, and a foreground notification should not
 * outstay the listening. Playing again from the app brings it back.
 */
export const NATIVE_PAUSED_IDLE_MS = 10 * 60_000;

/**
 * Set while the plugin may be running its service, in `sessionStorage`, which
 * lives as long as the WebView (and so the app process that owns the
 * service). A reload reads it to clear a notification the previous page left
 * behind, without touching the plugin on every app start.
 */
export const NATIVE_SESSION_MARKER_KEY = 'ora.audioOverview.nativeSession';

export type NativeSessionMarkerStorage = Pick<
  Storage,
  'getItem' | 'setItem' | 'removeItem'
>;

/** True when a previous page load may have left the service running. */
export const hasNativeSessionMarker = (
  storage: NativeSessionMarkerStorage | null
): boolean => {
  try {
    return storage?.getItem(NATIVE_SESSION_MARKER_KEY) === '1';
  } catch {
    return false;
  }
};

const writeMarker = (
  storage: NativeSessionMarkerStorage | null,
  isActive: boolean
) => {
  try {
    if (isActive) storage?.setItem(NATIVE_SESSION_MARKER_KEY, '1');
    else storage?.removeItem(NATIVE_SESSION_MARKER_KEY);
  } catch {
    // Without storage a reload cannot clear a leftover notification; the
    // next episode that loads still does.
  }
};

/** The Capacitor callback shape: data, or an error instead. */
type PluginCallback = (
  data?: { seekTime?: number | null } | null,
  error?: unknown
) => void;

const toActionDetails = (
  action: PluginAction,
  seekTime: number | null | undefined
): MediaSessionActionDetails => {
  const details: MediaSessionActionDetails = { action };
  if (typeof seekTime === 'number') details.seekTime = seekTime;

  if (action === 'seekbackward' || action === 'seekforward') {
    details.seekOffset = NATIVE_SEEK_OFFSET_SEC;
  }

  return details;
};

/**
 * A `navigator.mediaSession`-shaped facade over the plugin, so the engine
 * keeps one Media Session code path.
 *
 * The plugin runs its foreground service (and notification) while the state
 * it was last sent is `playing` or `paused`, and stops it on `none`. The
 * engine sends `none` on close, logout, the end of an episode and when other
 * media takes the speaker. On top of that, this facade:
 * - never starts the service for a pause (a paused load, an error), only for
 *   `playing`, which follows a tap;
 * - stops it after {@link NATIVE_PAUSED_IDLE_MS} of continuous pause.
 */
export const createPluginMediaSession = (
  plugin: MediaSessionPluginLike,
  markerStorage: NativeSessionMarkerStorage | null = null
): MediaSessionLike => {
  let metadata: MediaMetadata | null = null;
  let playbackState: MediaSessionPlaybackState = 'none';
  /** The state the plugin last received. */
  let nativeState: MediaSessionPlaybackState = 'none';
  let idleTimer: ReturnType<typeof setTimeout> | null = null;
  const handlers = new Map<PluginAction, MediaSessionActionHandler | null>();
  /** Native callbacks are registered once per action: each one stays alive. */
  const registered = new Set<PluginAction>();

  const sendState = (value: MediaSessionPlaybackState) => {
    nativeState = value;
    writeMarker(markerStorage, value !== 'none');
    send(() => plugin.setPlaybackState({ playbackState: value }));
  };

  const clearIdleTimer = () => {
    if (idleTimer === null) return;
    clearTimeout(idleTimer);
    idleTimer = null;
  };

  // The plugin outlives page loads (an update reload keeps the app process).
  // Stop any notification the previous page left behind before it lingers.
  sendState('none');

  return {
    get metadata() {
      return metadata;
    },
    set metadata(value: MediaMetadata | null) {
      metadata = value;

      // Always the bundled artwork: the plugin would download a URL itself.
      send(() =>
        plugin.setMetadata(
          value
            ? {
                title: value.title,
                artist: value.artist,
                album: value.album,
                artwork: [NATIVE_ARTWORK],
              }
            : { title: '', artist: '', album: '' }
        )
      );
    },

    get playbackState() {
      return playbackState;
    },
    set playbackState(value: MediaSessionPlaybackState) {
      playbackState = value;

      if (value !== 'paused') {
        clearIdleTimer();
        sendState(value);
        return;
      }

      if (nativeState === 'none') return;
      sendState('paused');

      // Counted from the first pause: repeated pauses do not extend it.
      if (idleTimer !== null) return;

      idleTimer = setTimeout(() => {
        idleTimer = null;
        sendState('none');
      }, NATIVE_PAUSED_IDLE_MS);
    },

    setActionHandler(action, handler) {
      // Same contract as the browser API, which the engine already handles.
      if (!isPluginAction(action)) {
        throw new TypeError(`Unsupported media session action: ${action}`);
      }

      // A removed handler keeps its native button until the service stops,
      // which the engine does in the same breath (close, yield): presses in
      // between are ignored.
      handlers.set(action, handler);
      if (!handler || registered.has(action)) return;
      registered.add(action);

      const onAction: PluginCallback = (data, error) => {
        if (error) return;
        handlers.get(action)?.(toActionDetails(action, data?.seekTime));
      };

      send(() => plugin.setActionHandler({ action }, onAction));
    },

    setPositionState(state) {
      send(() => plugin.setPositionState({ ...state }));
    },
  };
};

export interface NativeMediaSessionEnv {
  getPlatform: () => string;
  isPluginAvailable: (name: string) => boolean;
  plugin: MediaSessionPluginLike;
  /** Where the facade keeps {@link NATIVE_SESSION_MARKER_KEY}. */
  getMarkerStorage?: () => NativeSessionMarkerStorage | null;
}

/** `sessionStorage`, or null where reading it throws. */
export const getNativeSessionMarkerStorage =
  (): NativeSessionMarkerStorage | null => {
    try {
      return window.sessionStorage;
    } catch {
      return null;
    }
  };

const capacitorEnv = (): NativeMediaSessionEnv => ({
  getPlatform: () => Capacitor.getPlatform(),
  isPluginAvailable: (name) => Capacitor.isPluginAvailable(name),
  plugin: MediaSessionPlugin,
  getMarkerStorage: getNativeSessionMarkerStorage,
});

/**
 * The plugin-backed session on Android builds that ship the plugin; null
 * everywhere else, where `navigator.mediaSession` is the right surface. On
 * iOS the plugin registers its web implementation, so it counts as
 * "available" there: the platform check comes first.
 */
export const createNativeMediaSession = (
  env: NativeMediaSessionEnv = capacitorEnv()
): MediaSessionLike | null => {
  try {
    if (env.getPlatform() !== 'android') return null;
    if (!env.isPluginAvailable(NATIVE_MEDIA_SESSION_PLUGIN)) return null;
  } catch {
    return null;
  }

  return createPluginMediaSession(env.plugin, env.getMarkerStorage?.() ?? null);
};
